import type { ComponentType, ReactNode, SelectHTMLAttributes } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight, type LucideProps } from 'lucide-react'

import { cn } from '@/lib/utils/cn'

export type Icon = ComponentType<LucideProps>

/**
 * The button.
 *
 * Four roles, the same four every large console uses: one filled primary per view for the
 * thing you came to do, a bordered secondary for everything else, a quiet ghost for
 * tertiary actions in dense rows, and danger for the irreversible. Fixed heights rather
 * than padding, so a button sits on the same baseline as the input beside it.
 */
export function Button({
  children,
  onClick,
  variant = 'default',
  size = 'md',
  disabled,
  type = 'button',
  className,
  title,
  icon: IconComponent,
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'default' | 'primary' | 'danger' | 'ghost'
  size?: 'sm' | 'md'
  disabled?: boolean
  type?: 'button' | 'submit'
  className?: string
  title?: string
  /** Leading icon. Decorative: the label is the accessible name. */
  icon?: Icon
}) {
  const variants = {
    default:
      'border-line-strong bg-surface text-ink shadow-card hover:border-ink-faint hover:bg-hover'
      + ' active:bg-active',
    primary:
      'border-primary bg-primary text-ground shadow-card hover:border-primary-bright'
      + ' hover:bg-primary-bright active:bg-primary-dim active:text-ink',
    danger:
      'border-fail/30 bg-surface text-fail shadow-card hover:border-fail hover:bg-fail-wash'
      + ' active:bg-active',
    ghost:
      'border-transparent bg-transparent text-ink-muted hover:bg-hover hover:text-ink'
      + ' active:bg-active',
  }
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md',
        'border font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none',
        size === 'sm' ? 'h-8 px-2.5 text-xs' : 'h-9 px-3.5 text-sm',
        variants[variant],
        className,
      )}
    >
      {IconComponent ? (
        <IconComponent aria-hidden="true" className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
      ) : null}
      {children}
    </button>
  )
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string
  options: Array<{ value: string; label: string }>
}

/**
 * Filter select.
 *
 * The label sits beside the control rather than above it — a dense filter row with stacked
 * labels doubles in height for no gain in clarity.
 *
 * `appearance-none` plus a drawn chevron, because the native control's OS chrome differs
 * per platform and breaks the row's alignment. The `<select>` itself is kept, so keyboard
 * and screen-reader behaviour is the platform's rather than a reimplementation.
 */
export function Select({ label, options, className, ...props }: SelectProps) {
  return (
    <label className={cn('flex items-center gap-2', className)}>
      <span className="shrink-0 text-xs font-medium text-ink-muted">{label}</span>
      <span className="relative inline-flex items-center">
        <select
          {...props}
          className="h-8 appearance-none rounded-md border border-line-strong bg-surface py-1 pl-2.5
            pr-8 text-xs text-ink shadow-card transition-[border-color,box-shadow]
            hover:border-ink-faint focus:border-primary focus:outline-none focus:ring-[3px]
            focus:ring-primary/15"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-2 h-3.5 w-3.5 text-ink-muted"
        />
      </span>
    </label>
  )
}

export function Pagination({
  total,
  limit,
  offset,
  onChange,
}: {
  total: number
  limit: number
  offset: number
  onChange: (offset: number) => void
}) {
  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + limit, total)
  const canGoBack = offset > 0
  const canGoForward = to < total

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t
      border-line px-4 py-2.5">
      <p className="text-xs text-ink-muted">
        {total === 0 ? 'No rows' : `Showing ${from}–${to} of ${total.toLocaleString('en-US')}`}
      </p>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          icon={ChevronLeft}
          disabled={!canGoBack}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Previous
        </Button>
        <Button size="sm" disabled={!canGoForward} onClick={() => onChange(offset + limit)}>
          Next
          <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}
